import MuiChip from '@mui/material/Chip';
import type { AutocompleteRenderGetTagProps } from '@mui/material/Autocomplete';
import { useLayoutEffect, useRef, useState } from 'react';

/** Must match the `gap` on `.SmInputBase-root` / `.SmAutocomplete-tags` in Autocomplete.theme.tsx. */
const TAG_GAP = 4;
/** Must match the multiple input's `minWidth` in Autocomplete.theme.tsx. */
const INPUT_MIN_WIDTH = 30;
/** Below this, a single truncated tag is not worth showing — only the `+N` counter is. */
const MIN_TRUNCATED_TAG_WIDTH = 60;

const noop = () => undefined;

type FittedTagsProps<T> = {
  value: T[];
  getTagProps: AutocompleteRenderGetTagProps;
  getLabel: (option: T) => string;
};

/**
 * Selected values on a single line: as many tags as fit, then a `+N` counter for the rest.
 * A hidden copy of every tag is rendered to measure natural widths.
 */
export function FittedTags<T>({ value, getTagProps, getLabel }: FittedTagsProps<T>) {
  const rowRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(value.length);

  const labels = value.map(getLabel);
  const labelsKey = labels.join('\u0000');

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    const field = row?.parentElement;
    if (!row || !measure || !field) return;

    const update = () => setVisibleCount(countFittingTags(field, row, measure));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(field);
    return () => observer.disconnect();
  }, [labelsKey]);

  const hiddenCount = value.length - visibleCount;

  return (
    <span ref={rowRef} className="SmAutocomplete-tags">
      {labels.slice(0, visibleCount).map((label, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return <MuiChip key={key} size="small" label={label} {...tagProps} />;
      })}
      {hiddenCount > 0 ? (
        <MuiChip
          size="small"
          label={`+${hiddenCount}`}
          title={labels.slice(visibleCount).join(', ')}
          className="SmAutocomplete-tagCounter"
        />
      ) : null}
      <span ref={measureRef} className="SmAutocomplete-tagsMeasure" aria-hidden>
        {labels.map((label, index) => (
          <MuiChip
            key={index}
            size="small"
            label={label}
            onDelete={noop}
            tabIndex={-1}
            className="MuiAutocomplete-tag"
            // MUI focuses tags by `data-tag-index` on arrow keys and throws if none matches,
            // so hidden tags keep their index here.
            data-tag-index={index >= visibleCount ? index : undefined}
          />
        ))}
        <MuiChip size="small" label={`+${value.length}`} className="SmAutocomplete-tagCounter" />
      </span>
    </span>
  );
}

function countFittingTags(field: HTMLElement, row: HTMLElement, measure: HTMLElement): number {
  const fieldStyle = getComputedStyle(field);
  let available =
    field.clientWidth -
    parseFloat(fieldStyle.paddingInlineStart) -
    parseFloat(fieldStyle.paddingInlineEnd) -
    INPUT_MIN_WIDTH -
    TAG_GAP;

  // Subtract anything else in the field's flow (e.g. a start icon); end indicators are absolute.
  for (const child of Array.from(field.children) as HTMLElement[]) {
    if (child === row || child.tagName === 'INPUT' || getComputedStyle(child).position === 'absolute') continue;
    available -= child.offsetWidth + TAG_GAP;
  }

  const measured = Array.from(measure.children) as HTMLElement[];
  const counterWidth = measured[measured.length - 1].offsetWidth;
  const tagWidths = measured.slice(0, -1).map(chip => chip.offsetWidth);

  const allTagsWidth = tagWidths.reduce((sum, width) => sum + width, 0) + TAG_GAP * (tagWidths.length - 1);
  if (allTagsWidth <= available) return tagWidths.length;

  let used = counterWidth;
  let count = 0;
  while (count < tagWidths.length && used + TAG_GAP + tagWidths[count] <= available) {
    used += TAG_GAP + tagWidths[count];
    count++;
  }

  // No full tag fits: show the first one truncated if there's reasonable room for it.
  if (count === 0 && available - counterWidth - TAG_GAP >= MIN_TRUNCATED_TAG_WIDTH) return 1;
  return count;
}
